import { Checkbox, Col, ConfigProvider, Drawer, Flex, Input, Layout, Row, Select, Skeleton, Space, Table, Button, Tabs, Tooltip, notification, Modal } from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import type { TableProps } from "antd";
import { SorterResult } from "antd/es/table/interface";
import { LoaderSpinner } from "../../../components/common/Loader";
import { deleteCobPayer, getCobPayer, getCobPayerActions, getCobPayerAuditHistory, getCobPayerChangeRequests, getCobPayerFilters, getCobPayers, processCobPayerChangeRequest as processCobPayerChangeRequestApi } from "../../../service/httpsCalls";
import { ChangeRequestApprovalStatus, CobPayerPaging, CobPayerSort, CobPayerTabs, CobPayerValidation } from "./cobPayerConstants";
import CreateCobPayer, { CobPayerAuditHistory, CobPayerDetail, CobPayerSavedContext } from "./createCobPayer";
import { CobPayerAuditHistoryColumns, createCobPayerListColumns, renderCobPayerChangeRequestStatus } from "../tableConfig";

interface LookupItem {
    id: number;
    name: string;
}

interface LookupWithClientItem extends LookupItem {
    clientId: number;
}

interface ClearingHouseLookupItem {
    selectionValue: number;
    id: number | null;
    name: string;
    clientId: number;
    isDefault: boolean;
}

interface CobPayerActions {
    canEdit: boolean;
    canDelete: boolean;
    canApproveReject: boolean;
}

interface CobPayerChangeRequestDetail { id: number; fieldName: string; oldValue?: string; newValue?: string; }
interface CobPayerChangeRequest {
    id: number; approvalStatus: string; changeReason: string; requestedBy: string; requestedDate: string;
    rejectedReason?: string; reviewedBy?: string; reviewedDate?: string;
    details: CobPayerChangeRequestDetail[];
}

export interface CobPayerListItem {
    id: number;
    clientId: number;
    code: string;
    name: string;
    invoiceTypeId: number;
    invoiceType: string;
    changeRequestStatus: string;
    createdDate: string;
    modifiedDate?: string;
    changeRequestDate?: string;
    isActive: boolean;
}

type NotificationType = "success" | "info" | "warning" | "error";

export default function CobPayers() {
    const pendingApprovalStatus = ChangeRequestApprovalStatus[ChangeRequestApprovalStatus.Pending];
    const tableRef = useRef<HTMLDivElement>(null);
    const tableScrollTopRef = useRef<number | null>(null);
    const [api, contextHolder] = notification.useNotification();
    const [clientList, setClientList] = useState<LookupItem[]>([]);
    const [clearingHouseList, setClearingHouseList] = useState<ClearingHouseLookupItem[]>([]);
    const [invoiceTypeList, setInvoiceTypeList] = useState<LookupWithClientItem[]>([]);
    const [pdrCaseList, setPdrCaseList] = useState<LookupWithClientItem[]>([]);
    const [stateList, setStateList] = useState<LookupItem[]>([]);
    const [statusList, setStatusList] = useState<string[]>([]);
    const [canAdd, setCanAdd] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [canView, setCanView] = useState(false);
    const [canApproveReject, setCanApproveReject] = useState(false);
    const [showAddDrawer, setShowAddDrawer] = useState(false);
    const [showEditDrawer, setShowEditDrawer] = useState(false);
    const [activeEditTab, setActiveEditTab] = useState(CobPayerTabs.PayerInfo);
    const [selectedPayer, setSelectedPayer] = useState<CobPayerDetail | null>(null);
    const [payerActions, setPayerActions] = useState<CobPayerActions>({ canEdit: false, canDelete: false, canApproveReject: false });
    const [changeRequests, setChangeRequests] = useState<CobPayerChangeRequest[]>([]);
    const [expandedChangeRequestKeys, setExpandedChangeRequestKeys] = useState<number[]>([]);
    const [changeRequestsLoading, setChangeRequestsLoading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    const [auditHistory, setAuditHistory] = useState<CobPayerAuditHistory[]>([]);
    const [auditLoading, setAuditLoading] = useState(false);
    const [clientId, setClientId] = useState<number | null>(null);
    const [invoiceTypeId, setInvoiceTypeId] = useState<number | null>(null);
    const [code, setCode] = useState("");
    const [name, setName] = useState("");
    const [statuses, setStatuses] = useState<string[]>([]);
    const [activeOnly, setActiveOnly] = useState(false);
    const [items, setItems] = useState<CobPayerListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [filterLoading, setFilterLoading] = useState(false);
    const [filtersReady, setFiltersReady] = useState(false);
    const [pageSize, setPageSize] = useState(CobPayerPaging.DefaultPageSize);
    const [currentPage, setCurrentPage] = useState(CobPayerPaging.DefaultPageIndex);
    const [totalRecords, setTotalRecords] = useState(0);
    const [sortedInfo, setSortedInfo] = useState<SorterResult<CobPayerListItem>>({
        field: CobPayerSort.DefaultDateField,
        order: CobPayerSort.DefaultOrder
    });
    const [refreshToken, setRefreshToken] = useState(0);
    const [tableScrollY, setTableScrollY] = useState<number>();
    const [selectedPayerRowKey, setSelectedPayerRowKey] = useState<number | null>(null);

    const notify = (type: NotificationType, message: string) => {
        api[type]({ message });
    };

    const renderEllipsisCell = (value?: string) => value ? (
        <Tooltip title={value} placement="topLeft">
            <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {value}
            </span>
        </Tooltip>
    ) : "";

    const openEdit = (record: CobPayerListItem) => {
        const tableBody = tableRef.current?.querySelector<HTMLElement>(".ant-table-body");
        tableScrollTopRef.current = tableBody?.scrollTop ?? null;
        setSelectedPayerRowKey(record.id);
        setSelectedPayer(null);
        setPayerActions({ canEdit: false, canDelete: false, canApproveReject: false });
        setChangeRequests([]);
        setExpandedChangeRequestKeys([]);
        setAuditHistory([]);
        setActiveEditTab(CobPayerTabs.PayerInfo);
        setShowEditDrawer(true);
        setEditLoading(true);
        getCobPayer(record.id, (result: any) => {
            setEditLoading(false);
            if (result.hasError) {
                setShowEditDrawer(false);
                notify("error", result.error ?? "Unable to load COB Payer.");
                return;
            }

            setSelectedPayer(result.data);
        });
        getCobPayerActions(record.id, (result: any) => {
            if (result.hasError) {
                notify("error", result.error ?? "Unable to load COB Payer actions.");
                return;
            }

            setPayerActions(result.data ?? { canEdit: false, canDelete: false, canApproveReject: false });
        });
    };

    const closeEdit = (refreshGrid = false) => {
        setShowEditDrawer(false);
        setActiveEditTab(CobPayerTabs.PayerInfo);
        setSelectedPayer(null);
        setPayerActions({ canEdit: false, canDelete: false, canApproveReject: false });
        setDeleting(false);
        setAuditHistory([]);
        if (refreshGrid) {
            setRefreshToken(value => value + 1);
        }
    };

    const loadAuditHistory = () => {
        if (!selectedPayer) {
            return;
        }

        setAuditLoading(true);
        getCobPayerAuditHistory(selectedPayer.id, (result: any) => {
            setAuditLoading(false);
            if (result.hasError) {
                setAuditHistory([]);
                notify("error", result.error ?? "Unable to load COB Payer audit history.");
                return;
            }

            setAuditHistory(result.data ?? []);
        });
    };

    const loadChangeRequests = () => {
        if (!selectedPayer) return;
        setChangeRequestsLoading(true);
        getCobPayerChangeRequests(selectedPayer.id, (result: any) => {
            setChangeRequestsLoading(false);
            if (result.hasError) {
                notify("error", result.error ?? "Unable to load change requests.");
                return;
            }
            const requests: CobPayerChangeRequest[] = result.data ?? [];
            const requestIds = new Set(requests.map(request => request.id));
            const pendingRequestIds = requests
                .filter(request => request.approvalStatus === pendingApprovalStatus)
                .map(request => request.id);
            setChangeRequests(requests);
            setExpandedChangeRequestKeys(currentKeys => Array.from(new Set([
                ...currentKeys.filter(key => requestIds.has(key)),
                ...pendingRequestIds
            ])));
        });
    };

    const refreshGridFromFirstPage = (payerId?: number) => {
        setCurrentPage(CobPayerPaging.DefaultPageIndex);
        setSelectedPayerRowKey(payerId ?? null);
        tableScrollTopRef.current = 0;
        setRefreshToken(value => value + 1);
    };

    const refreshAfterChangeRequestAction = () => {
        if (!selectedPayer) return;

        const payerId = selectedPayer.id;
        setEditLoading(true);
        getCobPayer(payerId, (result: any) => {
            setEditLoading(false);
            if (result.hasError) {
                notify("error", result.error ?? "Unable to refresh COB Payer details.");
                return;
            }
            setSelectedPayer(result.data);
        });

        loadChangeRequests();
        loadAuditHistory();
        getCobPayerActions(payerId, (result: any) => {
            if (result.hasError) {
                notify("error", result.error ?? "Unable to refresh COB Payer actions.");
                return;
            }
            setPayerActions(result.data ?? { canEdit: false, canDelete: false, canApproveReject: false });
        });
        refreshGridFromFirstPage(payerId);
    };

    const processChangeRequest = (id: number, action: "approve" | "cancel") => {
        setChangeRequestsLoading(true);
        const callback = (result: any) => {
            if (result.hasError) {
                setChangeRequestsLoading(false);
                notify("error", result.error ?? `Unable to ${action} change request.`);
                return;
            }
            notify("success", `Change request ${action === "approve" ? "approved" : "cancelled"} successfully.`);
            refreshAfterChangeRequestAction();
        };
        const approvalStatus = action === "approve"
            ? ChangeRequestApprovalStatus.Approved
            : ChangeRequestApprovalStatus.Cancelled;
        processCobPayerChangeRequestApi(id, approvalStatus, null, callback);
    };

    const rejectChangeRequest = (id: number) => {
        let reason = "";
        Modal.confirm({
            title: "Reject Change Request",
            content: (
                <div style={{ paddingBottom: 24 }}>
                    <Input.TextArea
                        maxLength={CobPayerValidation.ChangeReasonMaxLength}
                        showCount
                        placeholder="Enter rejection reason"
                        onChange={event => { reason = event.target.value; }}
                    />
                </div>
            ),
            okText: "Reject",
            okButtonProps: { danger: true },
            onOk: close => {
                if (!reason.trim()) {
                    notify("warning", "Rejection reason is required.");
                    return;
                }
                return new Promise<void>((resolve, reject) => processCobPayerChangeRequestApi(
                    id, ChangeRequestApprovalStatus.Rejected, reason.trim(), (result: any) => {
                    if (result.hasError) {
                        notify("error", result.error ?? "Unable to reject change request.");
                        reject(new Error(result.error ?? "Unable to reject change request."));
                        return;
                    }
                    notify("success", "Change request rejected successfully.");
                    refreshAfterChangeRequestAction();
                    resolve();
                }));
            }
        });
    };

    const columns = useMemo(
        () => createCobPayerListColumns(openEdit, sortedInfo),
        [sortedInfo]
    );
    const clientInvoiceTypeList = useMemo(
        () => invoiceTypeList.filter(item => item.clientId === clientId),
        [clientId, invoiceTypeList]
    );

    const loadPayers = () => {
        setLoading(true);
        getCobPayers({
            pageIndex: currentPage,
            pageSize,
            sortField: sortedInfo.order ? sortedInfo.field : null,
            sortDescending: sortedInfo.order === "descend",
            clientId,
            invoiceTypeId,
            code: code.trim() || null,
            name: name.trim() || null,
            statuses,
            isActive: activeOnly ? true : null
        }, (res: any) => {
            setLoading(false);
            if (res === "error") {
                setItems([]);
                setTotalRecords(0);
                notify("error", "Unable to load COB payers.");
                return;
            }

            setItems(res.items ?? []);
            setTotalRecords(res.totalRecords ?? 0);
        });
    };

    const search = () => {
        if (!clientId) {
            notify("warning", "Please select a client.");
            return;
        }

        setSelectedPayerRowKey(null);
        tableScrollTopRef.current = null;
        if (currentPage !== 1) {
            setCurrentPage(CobPayerPaging.DefaultPageIndex);
        } else {
            setRefreshToken(value => value + 1);
        }
    };

    const resetFilters = () => {
        setClientId(clientList[0]?.id ?? null);
        setInvoiceTypeId(null);
        setCode("");
        setName("");
        setStatuses([]);
        setActiveOnly(false);
        setSortedInfo({
            field: CobPayerSort.DefaultDateField,
            order: CobPayerSort.DefaultOrder
        });
        setSelectedPayerRowKey(null);
        tableScrollTopRef.current = null;
        if (currentPage !== 1) {
            setCurrentPage(CobPayerPaging.DefaultPageIndex);
        } else {
            setRefreshToken(value => value + 1);
        }
    };

    const handleTableChange: TableProps<CobPayerListItem>["onChange"] = (pagination, _filters, sorter) => {
        const nextPage = pagination.current ?? 1;
        const nextPageSize = pagination.pageSize ?? 10;
        if (nextPage !== currentPage || nextPageSize !== pageSize) {
            setSelectedPayerRowKey(null);
            tableScrollTopRef.current = null;
        }
        setCurrentPage(nextPage);
        setPageSize(nextPageSize);
        setSortedInfo(sorter as SorterResult<CobPayerListItem>);
    };

    useEffect(() => {
        const previousBodyOverflow = document.body.style.overflow;
        const previousDocumentOverflow = document.documentElement.style.overflow;
        document.body.style.overflow = "hidden";
        document.documentElement.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = previousBodyOverflow;
            document.documentElement.style.overflow = previousDocumentOverflow;
        };
    }, []);

    useEffect(() => {
        setFilterLoading(true);
        getCobPayerFilters((res: any) => {
            setFilterLoading(false);
            if (res.hasError) {
                notify("error", "Unable to load COB payer filters.");
                return;
            }

            const clients = res.clientList ?? [];
            setClientList(clients);
            setClearingHouseList(res.clearingHouseList ?? []);
            setInvoiceTypeList(res.invoiceTypeList ?? []);
            setPdrCaseList(res.pdrCaseList ?? []);
            setStateList(res.stateList ?? []);
            setStatusList(res.statusList ?? []);
            setCanAdd(res.canAdd === true);
            setCanEdit(res.canEdit === true);
            setCanView(res.canView === true);
            setCanApproveReject(res.canApproveReject === true);
            setClientId(clients[0]?.id ?? null);
            setFiltersReady(true);
        });

        document.title = "COB Payer";
        const computeTableHeight = () => {
            const height = window.innerHeight - 470;
            setTableScrollY(height > 0 ? height : undefined);
        };
        computeTableHeight();
        window.addEventListener("resize", computeTableHeight);
        return () => window.removeEventListener("resize", computeTableHeight);
    }, []);

    useEffect(() => {
        if (filtersReady) {
            loadPayers();
        }
    }, [currentPage, pageSize, sortedInfo, filtersReady, refreshToken]);

    useEffect(() => {
        if (showEditDrawer || loading || selectedPayerRowKey == null || items.length === 0) {
            return;
        }

        const animationFrame = window.requestAnimationFrame(() => {
            const tableBody = tableRef.current?.querySelector<HTMLElement>(".ant-table-body");
            if (tableBody && tableScrollTopRef.current != null) {
                tableBody.scrollTop = tableScrollTopRef.current;
            }

            const selectedRow = Array.from(tableRef.current?.querySelectorAll<HTMLElement>("tr[data-row-key]") ?? [])
                .find(row => row.dataset.rowKey === String(selectedPayerRowKey));
            selectedRow?.scrollIntoView({ block: "nearest" });
        });

        return () => window.cancelAnimationFrame(animationFrame);
    }, [items, loading, selectedPayerRowKey, showEditDrawer]);

    const handlePayerSaved = (savedPayer: CobPayerSavedContext) => {
        setShowAddDrawer(false);
        setClientId(savedPayer.clientId);
        setInvoiceTypeId(savedPayer.invoiceTypeId);
        setCode("");
        setName("");
        setStatuses([]);
        setActiveOnly(false);
        setSortedInfo({
            field: CobPayerSort.DefaultDateField,
            order: CobPayerSort.DefaultOrder
        });
        setCurrentPage(CobPayerPaging.DefaultPageIndex);
        setSelectedPayerRowKey(savedPayer.id);
        tableScrollTopRef.current = 0;
        setRefreshToken(value => value + 1);
    };

    const handlePayerUpdated = () => {
        const payerId = selectedPayer?.id;
        closeEdit();
        refreshGridFromFirstPage(payerId);
    };

    const confirmDeletePayer = () => {
        if (!selectedPayer || !payerActions.canDelete) {
            return;
        }

        Modal.confirm({
            title: `Delete COB Payer: ${selectedPayer.code} - ${selectedPayer.name}?`,
            content: "This COB Payer will be deleted.",
            okText: "Yes",
            cancelText: "No",
            okButtonProps: { danger: true },
            onOk: () => new Promise<void>((resolve) => {
                setDeleting(true);
                deleteCobPayer(selectedPayer.id, (result: any) => {
                    setDeleting(false);
                    if (result.hasError) {
                        notify("error", result.error ?? "Unable to delete COB Payer.");
                        resolve();
                        return;
                    }

                    notify("success", "COB Payer deleted successfully.");
                    const moveToPreviousPage = items.length === 1 && currentPage > 1;
                    closeEdit();
                    setSelectedPayerRowKey(null);
                    tableScrollTopRef.current = null;
                    if (moveToPreviousPage) {
                        setCurrentPage(page => page - 1);
                    } else {
                        setRefreshToken(value => value + 1);
                    }
                    resolve();
                });
            })
        });
    };

    return (
        <ConfigProvider theme={{ components: { Layout: { bodyBg: "#eff1f5", siderBg: "#fff" } } }}>
            {contextHolder}
            <Layout className="cob-payer-list-page" style={{ padding: "20px", minWidth: 0 }}>
                <Drawer
                    title="Add COB Payer"
                    destroyOnClose
                    maskClosable={false}
                    open={showAddDrawer}
                    width={window.innerWidth < 768 ? "100%" : Math.max(window.innerWidth * 0.7, 1000)}
                    onClose={() => setShowAddDrawer(false)}
                    styles={{ body: { background: "#eff1f5", overflowY: "auto" } }}
                >
                    <CreateCobPayer
                        clientList={clientList}
                        clearingHouseList={clearingHouseList}
                        invoiceTypeList={invoiceTypeList}
                        pdrCaseList={pdrCaseList}
                        stateList={stateList}
                        onSaved={handlePayerSaved}
                        onCancel={() => setShowAddDrawer(false)}
                        notify={notify}
                    />
                </Drawer>
                <Drawer
                    title={(activeEditTab === CobPayerTabs.ChangeRequests || activeEditTab === CobPayerTabs.AuditHistory) && selectedPayer
                        ? `Edit COB Payer: ${selectedPayer.code} - ${selectedPayer.name}`
                        : "Edit COB Payer"}
                    destroyOnClose
                    maskClosable={false}
                    open={showEditDrawer}
                    width={window.innerWidth < 768 ? "100%" : Math.max(window.innerWidth * 0.7, 1000)}
                    onClose={() => closeEdit(true)}
                    styles={{ body: { background: "#eff1f5", overflowY: "auto" } }}
                >
                    <Skeleton loading={editLoading} active>
                        <div>
                            {selectedPayer && (
                                <Tabs
                                    type="card"
                                    activeKey={activeEditTab}
                                    style={{ width: "100%", background: "#fff" }}
                                    tabBarStyle={{ marginBottom: "0px" }}
                                    onChange={(key) => {
                                        setActiveEditTab(key);
                                        if (key === CobPayerTabs.AuditHistory) {
                                            loadAuditHistory();
                                        }
                                        if (key === CobPayerTabs.ChangeRequests) {
                                            loadChangeRequests();
                                        }
                                    }}
                                    items={[
                                        {
                                            key: CobPayerTabs.PayerInfo,
                                            label: "COB Payer Info",
                                            children: (
                                                <CreateCobPayer
                                                    clientList={clientList}
                                                    clearingHouseList={clearingHouseList}
                                                    invoiceTypeList={invoiceTypeList}
                                                    pdrCaseList={pdrCaseList}
                                                    stateList={stateList}
                                                    payer={selectedPayer}
                                                    canEdit={payerActions.canEdit}
                                                    onSaved={handlePayerUpdated}
                                                    onCancel={() => closeEdit(true)}
                                                    canDelete={payerActions.canDelete}
                                                    canApproveReject={payerActions.canApproveReject}
                                                    deleting={deleting}
                                                    onDelete={confirmDeletePayer}
                                                    notify={notify}
                                                />
                                            )
                                        },
                                        {
                                            key: CobPayerTabs.ChangeRequests,
                                            label: "Change Requests",
                                            children: (
                                                <Table<CobPayerChangeRequest>
                                                    style={{ marginTop: "12px" }}
                                                    dataSource={changeRequests}
                                                    rowKey="id"
                                                    size="small"
                                                    tableLayout="fixed"
                                                    scroll={{ x: 1410 }}
                                                    loading={{
                                                        indicator: <LoaderSpinner size={24} topMargin="10px" />,
                                                        spinning: changeRequestsLoading
                                                    }}
                                                    expandable={{
                                                        expandedRowKeys: expandedChangeRequestKeys,
                                                        onExpandedRowsChange: keys => {
                                                            const pendingRequestIds = changeRequests
                                                                .filter(request => request.approvalStatus === pendingApprovalStatus)
                                                                .map(request => request.id);
                                                            setExpandedChangeRequestKeys(Array.from(new Set([
                                                                ...Array.from(keys, key => Number(key)),
                                                                ...pendingRequestIds
                                                            ])));
                                                        },
                                                        expandedRowRender: request => (
                                                        <div className="cob-payer-change-request-detail-grid">
                                                        <Table<CobPayerChangeRequestDetail>
                                                            dataSource={request.details}
                                                            rowKey="id"
                                                            size="small"
                                                            pagination={false}
                                                            tableLayout="fixed"
                                                            columns={[
                                                                { title: "Field Name", dataIndex: "fieldName", ellipsis: { showTitle: false }, render: renderEllipsisCell },
                                                                { title: "Old Value", dataIndex: "oldValue", ellipsis: { showTitle: false }, render: renderEllipsisCell },
                                                                { title: "New Value", dataIndex: "newValue", ellipsis: { showTitle: false }, render: renderEllipsisCell }
                                                            ]}
                                                        />
                                                        </div>
                                                    ) }}
                                                    columns={[
                                                        { title: "Status", dataIndex: "approvalStatus", width: 140, render: renderCobPayerChangeRequestStatus },
                                                        {
                                                            title: "Reason for Change",
                                                            dataIndex: "changeReason",
                                                            width: 230,
                                                ellipsis: { showTitle: false },
                                                render: renderEllipsisCell
                                                        },
                                                        { title: "Requested By", dataIndex: "requestedBy", width: 150, ellipsis: true },
                                                        { title: "Requested Date", dataIndex: "requestedDate", width: 170, render: value => value ? dayjs(value).format("MM/DD/YYYY hh:mm A") : "" },
                                                        { title: "Rejected Reason", dataIndex: "rejectedReason", width: 180, ellipsis: { showTitle: false }, render: renderEllipsisCell },
                                                        { title: "Reviewed By", dataIndex: "reviewedBy", width: 150, ellipsis: true },
                                                        { title: "Reviewed Date", dataIndex: "reviewedDate", width: 170, render: value => value ? dayjs(value).format("MM/DD/YYYY hh:mm A") : "" },
                                                        {
                                                            title: "Actions",
                                                            width: 220,
                                                            fixed: "right",
                                                            render: (_, request) => request.approvalStatus === pendingApprovalStatus ? (
                                                                <Space>
                                                                    {payerActions.canApproveReject && <Button size="small" type="primary" onClick={() => processChangeRequest(request.id, "approve")}>Approve</Button>}
                                                                    {payerActions.canApproveReject && <Button size="small" danger onClick={() => rejectChangeRequest(request.id)}>Reject</Button>}
                                                                    {payerActions.canEdit && <Button size="small" onClick={() => processChangeRequest(request.id, "cancel")}>Cancel</Button>}
                                                                </Space>
                                                            ) : null
                                                        }
                                                    ]}
                                                />
                                            )
                                        },
                                        {
                                            key: CobPayerTabs.AuditHistory,
                                            label: "Audit History",
                                            children: (
                                                <Table<CobPayerAuditHistory>
                                                    style={{ marginTop: "12px", overflowY: "auto", height: "auto", background: "#fff" }}
                                                    tableLayout="fixed"
                                                    dataSource={auditHistory}
                                                    columns={CobPayerAuditHistoryColumns}
                                                    scroll={{ x: 1230 }}
                                                    rowKey="id"
                                                    size="small"
                                                    loading={{ indicator: <LoaderSpinner size={24} topMargin="10px" />, spinning: auditLoading }}
                                                    pagination={{ pageSize: CobPayerPaging.DefaultPageSize, showSizeChanger: true }}
                                                />
                                            )
                                        }
                                    ]}
                                />
                            )}
                        </div>
                    </Skeleton>
                </Drawer>
                <h3 style={{ textTransform: "uppercase", marginTop: 0 }}>COB Payer</h3>
                <Flex justify="space-between" style={{ padding: "1rem", alignItems: "center", background: "#fff" }}>
                    <label><SearchOutlined />&nbsp;Search</label>
                    {canAdd && (
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowAddDrawer(true)}>
                            Add Payer
                        </Button>
                    )}
                </Flex>
                <Skeleton loading={filterLoading} active>
                    <div style={{ background: "#fff", padding: "0 0.5rem 1rem", marginBottom: "2rem", minWidth: 0 }}>
                        <Row gutter={[16, 16]} style={{ marginLeft: 0, marginRight: 0 }}>
                        <Col xs={24} md={6}>
                            <Select
                                style={{ width: "100%" }}
                                placeholder="Select a Client"
                                value={clientId}
                                onChange={(value) => {
                                    setClientId(value);
                                    setInvoiceTypeId(null);
                                }}
                                options={clientList.map(item => ({ value: item.id, label: item.name }))}
                            />
                        </Col>
                        <Col xs={24} md={6}>
                            <Select
                                style={{ width: "100%" }}
                                placeholder="Select Invoice Submission Type"
                                value={invoiceTypeId}
                                onChange={setInvoiceTypeId}
                                options={clientInvoiceTypeList.map(item => ({ value: item.id, label: item.name }))}
                                allowClear
                            />
                        </Col>
                        <Col xs={24} md={6}>
                            <Input
                                value={code}
                                placeholder="Payer Code"
                                allowClear
                                onChange={event => setCode(event.target.value.toUpperCase())}
                            />
                        </Col>
                        <Col xs={24} md={6}>
                            <Input
                                value={name}
                                placeholder="Payer Name"
                                allowClear
                                onChange={event => setName(event.target.value)}
                            />
                        </Col>
                        <Col xs={24} md={6}>
                            <Select
                                mode="multiple"
                                style={{ width: "100%" }}
                                placeholder="Select Change Request Status"
                                value={statuses}
                                onChange={setStatuses}
                                options={statusList.map(status => ({ value: status, label: status }))}
                                allowClear
                            />
                        </Col>
                        <Col xs={24} md={6}>
                            <Checkbox checked={activeOnly} onChange={event => setActiveOnly(event.target.checked)}>
                                Active only
                            </Checkbox>
                        </Col>
                        </Row>
                        <Flex justify="end" style={{ padding: "1rem 0.5rem 0" }} gap="small">
                            <Button onClick={search} loading={loading} type="primary">Search</Button>
                            <Button onClick={resetFilters}>Reset</Button>
                        </Flex>
                    </div>
                </Skeleton>
                <div ref={tableRef} className="cob-payer-list-table">
                <Table<CobPayerListItem>
                    style={{ background: "#fff", width: "100%", maxWidth: "100%" }}
                    tableLayout="fixed"
                    scroll={{ x: 1500, y: items.length > 0 ? tableScrollY : undefined }}
                    dataSource={items}
                    columns={columns}
                    rowKey="id"
                    size="small"
                    loading={{ indicator: <LoaderSpinner size={24} topMargin="10px" />, spinning: loading }}
                    pagination={{
                        current: currentPage,
                        pageSize,
                        total: totalRecords,
                        showTotal: (total, range) => `${range[0] || 0}-${range[1] || 0} of ${total} items`,
                        showSizeChanger: true
                    }}
                    onChange={handleTableChange}
                    rowClassName={(record) => record.id === selectedPayerRowKey ? "ant-table-row-selected" : ""}
                />
                </div>
            </Layout>
        </ConfigProvider>
    );
}
